import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { NewPasswordComponent } from '../../shared/components/password/new-password.component';
import { PasswordPattern } from '../../_interfaces/password-pattern';
import { SPECIAL_CHARS_REGEX } from '../../_helpers/user.helper';

@Component({
  selector: 'app-password-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatButtonModule, MatIconModule, TranslateModule, LoadingSpinnerComponent,
    NewPasswordComponent,
  ],
  host: { class: 'flex-1 flex flex-col min-h-0' },
  template: `
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 overflow-auto min-h-0 flex flex-col gap-2">
        <div class="mb-6">
          <h2 class="headline-medium text-primary mb-1 hidden md:block" style="font-family: 'DM Sans', sans-serif">{{ 'Reset password' | translate }}</h2>
          <p class="body-large text-on-surface-variant">{{ 'Please create a new password' | translate }}</p>
        </div>

        <!-- Current Password -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>{{ 'Current password' | translate }}</mat-label>
          <input
            matInput
            [type]="showCurrentPassword ? 'text' : 'password'"
            formControlName="currentPassword"
            [placeholder]="'Enter current password' | translate"
          />
          <button
            mat-icon-button matSuffix type="button"
            (click)="showCurrentPassword = !showCurrentPassword"
            class="text-on-surface-variant"
          >
            <mat-icon [svgIcon]="showCurrentPassword ? 'eye_opened' : 'eye_closed'"></mat-icon>
          </button>
          @if (form.get('currentPassword')?.hasError('required') && form.get('currentPassword')?.touched) {
            <mat-error>{{ 'Current password is required' | translate }}</mat-error>
          } @else if (form.get('currentPassword')?.hasError('serverError')) {
            <mat-error>{{ 'Current password is incorrect' | translate }}</mat-error>
          }
        </mat-form-field>

        <!-- New Password -->
        <app-new-password
          #newPassword
          [control]="form.get('newPassword')"
          [passwordPattern]="passwordPattern"
          [confirmPasswordValue]="form.get('confirmPassword')?.value || ''"
          [label]="'New password' | translate"
          [placeholder]="'Enter new password' | translate"
          class="pb-3"
        ></app-new-password>

        <!-- Confirm Password -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>{{ 'Confirm password' | translate }}</mat-label>
          <input
            matInput
            [type]="showConfirmPassword ? 'text' : 'password'"
            formControlName="confirmPassword"
            [placeholder]="'Confirm new password' | translate"
          />
          <button
            mat-icon-button matSuffix type="button"
            (click)="showConfirmPassword = !showConfirmPassword"
            class="text-on-surface-variant"
          >
            <mat-icon [svgIcon]="showConfirmPassword ? 'eye_opened' : 'eye_closed'"></mat-icon>
          </button>
          @if (form.get('confirmPassword')?.hasError('required') && form.get('confirmPassword')?.touched) {
            <mat-error>{{ 'Please confirm your password' | translate }}</mat-error>
          }
        </mat-form-field>

      </div>

      <div class="flex items-center justify-between gap-4 pt-4 shrink-0">
        <div class="hidden md:block w-1/2">
          <button mat-stroked-button type="button" class="w-full" (click)="goBack.emit()">
            {{ 'Go back' | translate }}
          </button>
        </div>

        <button
          mat-flat-button
          color="primary"
          type="submit"
          class="flex-1 whitespace-nowrap"
          [disabled]="!isFormValid() || loading"
        >
          <app-loading-spinner [loading]="loading" [diameter]="20">
            {{ 'Change password' | translate }}
          </app-loading-spinner>
        </button>
      </div>
    </form>
  `,
})
export class PasswordStepComponent implements OnInit, OnChanges {
  @Input() passwordPattern!: PasswordPattern;
  @Input() loading = false;
  @Input() currentPasswordError: string | null = null;
  @Output() changePassword = new EventEmitter<{ currentPassword: string; newPassword: string }>();
  @Output() goBack = new EventEmitter<void>();

  form!: FormGroup;
  showCurrentPassword = false;
  showConfirmPassword = false;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    });

    // Clear server error when user edits the current password
    this.form.get('currentPassword')?.valueChanges.subscribe(() => {
      const ctrl = this.form.get('currentPassword');
      if (ctrl?.hasError('serverError')) {
        ctrl.setErrors(null);
        ctrl.updateValueAndValidity();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentPasswordError']?.currentValue) {
      // Set a custom error on the form control so mat-error becomes visible
      this.form?.get('currentPassword')?.setErrors({ serverError: true });
      this.form?.get('currentPassword')?.markAsTouched();
    }
  }

  isFormValid(): boolean {
    const { currentPassword, newPassword, confirmPassword } = this.form.value;
    if (!currentPassword || !newPassword || !confirmPassword) return false;
    const minLength = this.passwordPattern?.passwordLength || 6;
    if (newPassword.length < minLength) return false;
    if (/\s/.test(newPassword)) return false;
    if (this.passwordPattern?.passwordLowercase && !/[a-z]/.test(newPassword)) return false;
    if (this.passwordPattern?.passwordUppercase && !/[A-Z]/.test(newPassword)) return false;
    if (this.passwordPattern?.passwordNumbers && !/[0-9]/.test(newPassword)) return false;
    if (this.passwordPattern?.passwordSymbols && !SPECIAL_CHARS_REGEX.test(newPassword)) return false;
    if (newPassword !== confirmPassword) return false;
    return true;
  }

  onSubmit(): void {
    if (!this.isFormValid()) return;
    const { currentPassword, newPassword } = this.form.value;
    this.changePassword.emit({ currentPassword, newPassword });
  }
}
